import React, { ReactNode, useEffect, useMemo, useReducer, useRef } from 'react';
import classNames from 'classnames';
import { Loader2 } from 'lucide-react';
import { isEqual, pick } from 'lodash';
import TableBody from './components/main/components/table-body/TableBody';
import TableHeader from './components/main/components/table-header/TableHeader';
import TablePagination from './components/table-pagination/TablePagination';
import { defaultDataTableState, tableReducer } from './dataTableReducer';
import styles from './DataTable.module.scss';
import { type AllTableActions, type DataTableState, type DataTableColumns, type TableRows, TableRowData } from './dataTableTypes';
import Table from '../table/Table';

type DownloadHandler = (props: Pick<TableContextType, 'rows' | 'columns' | 'downloadFileName'>) => Promise<void>;

export type TableContextType = DataTableState & {
    columns: DataTableColumns;
    emptyStateMessage?: ReactNode;
    downloadFileName?: string;
    rows: TableRows;
    totalRowsForPagination: number;
    allowSortClear?: boolean;
    dispatch: React.Dispatch<AllTableActions>;
    downloadHandler?: DownloadHandler;
    onRowClick?: (rowData: TableRowData) => void;
    onPageChange?: (pageNumber: number) => void;
    onSort?: (columnId: string) => void;
    isSelectable?: boolean;
    selectableColumnIndex?: number;
    isServerSideSort?: boolean;
    disableRowHover?: boolean;
};

export const TableContext = React.createContext(null as TableContextType | null);

export type TableProps = {
    state?: Partial<DataTableState>;
    emptyStateMessage?: ReactNode;
    columns: DataTableColumns;
    className?: string;
    downloadFileName?: string;
    allowSortClear?: boolean;
    downloadHandler?: DownloadHandler;
    onStateChange?: (newState: DataTableState) => void;
    onRowSelect?: (rowData: TableRowData) => void;
    onPageChange?: (pageNumber: number) => void;
    onSort?: (columnId: string) => void;
    totalRowsForPagination?: number;
    tableClassName?: string;
    isSelectable?: boolean;
    selectableColumnIndex?: number;
    /** When true, shows a translucent overlay with a spinner over the table (header + body), not pagination */
    isLoadingOverlay?: boolean;
    /** When true, skip client-side sorting — the server API already returns sorted results. */
    isServerSideSort?: boolean;
    /** When true, rows don't change background on hover/active. */
    disableRowHover?: boolean;
};
const DataTable = (props: TableProps) => {
    const initialState = useMemo(
        () => ({
            ...defaultDataTableState,
            ...(props.state || {}),
        }),
        [props.state]
    );

    const [tableState, dispatch] = useReducer(tableReducer, initialState);
    const isRestoringFromParentRef = useRef(false);
    const lastEmittedStateRef = useRef<DataTableState | null>(null);

    /*
        The useReducer used above does not update the tableState when the parent component updates the state.
        To fix this, we need to check if state property has changed by the parent and reset it if necessary.
    */
    useEffect(() => {
        // Only compare the subset of state that the parent controls
        const controlledKeys = Object.keys(props.state || {});
        const currentControlledState = pick(tableState, controlledKeys);
        if (isEqual(props.state || {}, currentControlledState)) {
            return;
        }
        // Parent requested a state change → restore from parent-provided initialState
        isRestoringFromParentRef.current = true;
        dispatch({
            type: 'restore_defaults',
            payload: { ...initialState },
        });
    }, [props.state]);

    useEffect(() => {
        // Skip emitting back to parent immediately after a parent-driven restore
        if (isRestoringFromParentRef.current) {
            isRestoringFromParentRef.current = false;
            return;
        }
        // Avoid emitting identical state repeatedly
        if (!isEqual(lastEmittedStateRef.current, tableState)) {
            // Only skip the first emission if it matches initial state (on mount)
            if (lastEmittedStateRef.current === null && isEqual(initialState, tableState)) {
                lastEmittedStateRef.current = tableState;
                return;
            }
            lastEmittedStateRef.current = tableState;
            props.onStateChange?.(tableState);
        }
    }, [tableState]);

    const wrapperClasses = classNames(styles.wrapper, props.className);
    const tableClasses = classNames(styles.table, props.tableClassName);

    /*
        The below code is to make pagination work with the API.
        The API will return the total number of rows, and the current page number.
        For rowsPerPage value of 3, and current page number of 2, the data should be positioned as follows:
        [
            [emptyRow, emptyRow, emptyRow],
            [row1, row2, row3],
            [emptyRow, emptyRow, emptyRow],
        ]
    */
    // No need to add extra column for isSelectable since emptyRow is based on columns, not rendered columns
    const emptyRow = Array.from({ length: props.columns.length }, () => '');
    const currentPage = tableState.currentPageNumber;

    let positionedData = tableState.data;
    if (props.totalRowsForPagination && tableState.data.length !== props.totalRowsForPagination) {
        const fillEmptyRows = Array.from(
            { length: props.totalRowsForPagination - ((currentPage - 1) * tableState.rowsPerPage + tableState.data.length) },
            () => emptyRow
        );
        positionedData = [
            ...Array.from({ length: (currentPage - 1) * tableState.rowsPerPage }, () => emptyRow),
            ...tableState.data,
            ...fillEmptyRows,
        ].filter(Boolean);
    }

    return (
        <TableContext.Provider
            value={{
                ...tableState,
                dispatch,
                columns: props.columns,
                emptyStateMessage: props.emptyStateMessage,
                rows: positionedData.map((row, index) => ({
                    rowId: index,
                    cells: row,
                })),
                allowSortClear: props.allowSortClear === undefined ? true : props.allowSortClear,
                downloadFileName: props.downloadFileName,
                totalRowsForPagination: props.totalRowsForPagination || tableState.data.length,
                downloadHandler: props.downloadHandler,
                onRowClick: (rowData: TableRowData) => props.onRowSelect?.(rowData),
                onPageChange: props.onPageChange,
                onSort: props.onSort,
                isSelectable: props.isSelectable,
                selectableColumnIndex: props.selectableColumnIndex ?? 0,
                isServerSideSort: props.isServerSideSort === true,
                disableRowHover: props.disableRowHover === true,
            }}
        >
            <div className={wrapperClasses} data-testid='datatable'>
                <div className={styles.tableScrollRegion}>
                    <div className={styles.tableContainer}>
                        <Table className={tableClasses} stickyHeader={true}>
                            <TableHeader />
                            <TableBody />
                        </Table>
                    </div>
                    {props.isLoadingOverlay && (
                        <div
                            className={styles.loadingOverlay}
                            data-testid='table-loading-overlay'
                            aria-busy='true'
                            aria-label='Loading table'
                        >
                            <Loader2 className={styles.loadingSpinner} role='progressbar' />
                        </div>
                    )}
                </div>
                <TablePagination />
            </div>
        </TableContext.Provider>
    );
};

export default DataTable;
